import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send } from 'lucide-react'; // Import ikony Send
import DropZone from '../DropZone';
import "./AdminOrderDetails.css";
import Popup from './common/Popup'; // Corrected import path for Popup

// Import new hooks
import { useOrderDetailsUIState } from '../hooks/useOrderDetailsUIState'; // Remove PopupComponent import from here
import { useOrderDetailsData } from '../hooks/useOrderDetailsData';
import { useOrderDetailsActions } from '../hooks/useOrderDetailsActions';

// Import new components
import OrderInfoSection from './OrderInfoSection';
import OrderFilesTable from './OrderFilesTable';
import AddProjectSection from './AddProjectSection';
import ProductionFilesTable from './ProductionFilesTable';
import MetadataModal from './MetadataModal';
import MissingFontSection from './MissingFontSection';
import AiInteractionModals from './AiInteractionModals';
import EditorOverlayWrapper from './EditorOverlayWrapper'; // Import komponentu EditorOverlayWrapper
// PopupComponent is now exported from useOrderDetailsUIState

export default function OrderDetails({ backendUrl }) {
  // Stany do obsługi EditorOverlayWrapper
  const [showEditorOverlay, setShowEditorOverlay] = useState(false);
  const [editorOverlayProps, setEditorOverlayProps] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  // --- Hooks ---
  const uiState = useOrderDetailsUIState();
  // PopupComponent is imported directly now, remove from destructuring
  const { showPopup, hidePopup, ...restUiState } = uiState;

  const dataState = useOrderDetailsData(id, backendUrl, showPopup);
  const { order, files, statuses, templates, editedQuantities, editedEmail, isLoadingOrder, isLoadingTemplates, ...restDataState } = dataState;

  // Prepare data updaters object for actions hook
  const dataUpdaters = {
      setOrder: dataState.setOrder,
      setFiles: dataState.setFiles,
      setPdfMetadatas: dataState.setPdfMetadatas,
      setEditedQuantities: dataState.setEditedQuantities,
      setEditedEmail: dataState.setEditedEmail, // Pass setter for email
      fetchAllFonts: dataState.fetchAllFonts, // Pass font refresh function
      fetchOrderData: dataState.fetchOrderData, // Dodano przekazanie funkcji odświeżania
  };

  // Prepare UI actions/setters object for OrderFilesTable etc.
   const uiActions = {
      handleToggleGuests: restUiState.handleToggleGuests,
      setGuestsTextMap: restUiState.setGuestsTextMap,
      handleOpenAiModal: restUiState.handleOpenAiModal,
      handleShowLogs: restUiState.handleShowLogs,
      showPopup: showPopup,
      // Pass other necessary UI setters if needed by subcomponents
  };

  const actions = useOrderDetailsActions(
    id,
    backendUrl,
    { order, files, editedQuantities, guestsTextMap: restUiState.guestsTextMap }, // Pass necessary data
    { showPopup, hidePopup, ...restUiState }, // Pass all UI state and helpers
    dataUpdaters, // Pass data setters
    navigate
  );

  // --- Event Handlers ---
  const handleGoBack = () => {
    navigate("/admin");
  };

  // --- Render Logic ---
  if (isLoadingOrder) {
    return (
      <div className="order-details-container">
        <p>Wczytywanie...</p>
        {/* Render Popup even during loading to show potential fetch errors */}
        <Popup config={uiState.popupConfig} onClose={hidePopup} />
      </div>
    );
  }

  // Pass necessary props to sub-components
  const orderInfoProps = {
    order,
    isEditingEmail: restUiState.isEditingEmail,
    editedEmail: dataState.editedEmail, // Use email from dataState
    onEditEmailClick: restUiState.handleEditEmailClick,
    onSaveEmail: actions.handleSaveEmail, // Pass action handler
    onCancelEditEmail: restUiState.handleCancelEditEmail,
    onCopyPublicLink: actions.handleCopyPublicLink,
    onCopyOrderName: actions.handleCopyOrderName,
    onCopyEmail: actions.handleCopyEmail,
    onEmailChange: dataUpdaters.setEditedEmail, // Pass setter directly
    onSendProjectToClient: actions.handleSendProjectToClient // Przekazanie nowej akcji
  };

  const orderFilesTableProps = {
    files,
    statuses,
    editedQuantities: dataState.editedQuantities, // Use quantities from dataState
    guestsOpenMap: restUiState.guestsOpenMap,
    guestsTextMap: restUiState.guestsTextMap,
    guestsLoadingMap: restUiState.guestsLoadingMap,
    aiStateMap: restUiState.aiStateMap,
    duplicatingFileId: restUiState.duplicatingFileId,
    deletingFileId: restUiState.deletingFileId,
    actions: { 
      ...actions, 
      backendUrl,
      // Przekazujemy funkcję handlePreviewPdf z dodatkowymi parametrami
      handlePreviewPdf: (downloadKey) => actions.handlePreviewPdf(downloadKey, setShowEditorOverlay, setEditorOverlayProps)
    }, // Pass all actions, add backendUrl if needed by download links
    uiActions: uiActions, // Pass UI action handlers/setters
    dataActions: { setEditedQuantities: dataUpdaters.setEditedQuantities } // Pass necessary data setters
  };

  const addProjectSectionProps = {
    templates,
    templateSearchQuery: restUiState.templateSearchQuery,
    onSearchChange: restUiState.setTemplateSearchQuery,
    onAddProject: actions.handleAddProjectFromTemplate,
    isLoading: isLoadingTemplates,
    addingProjectId: restUiState.addingProjectFromTemplateId
  };

  const productionFilesTableProps = {
    files,
    backendUrl
  };

  const metadataModalProps = {
    metadata: restUiState.selectedMetadata,
    onClose: restUiState.handleCloseMetadata
  };

  const missingFontSectionProps = {
      missingFontToAdd: restUiState.missingFontToAdd,
      missingFontFile: restUiState.missingFontFile,
      onFileChange: restUiState.setMissingFontFile,
      onSubmit: actions.handleAddMissingFont,
      onCancel: () => {
          restUiState.setMissingFontToAdd(null);
          restUiState.setMissingFontFile(null);
      }
  };

  const selectedFileForAi = files.find(f => f.id === restUiState.showAiModalForProjectId);
  const initialAiContentForModal = selectedFileForAi ? selectedFileForAi.initialAiContent : '';

  const aiInteractionModalsProps = {
    showAiModalForProjectId: restUiState.showAiModalForProjectId,
    // aiRequestText: restUiState.aiRequestText, // Removed
    // onAiRequestTextChange: restUiState.setAiRequestText, // Removed
    onCloseAiModal: restUiState.handleCloseAiModal,
    onSubmitAiRequest: actions.handleAiRequestSubmit, // Pass action handler (will now receive text)
    showLogsModalConfig: restUiState.showLogsModalConfig,
    logsLoading: restUiState.logsLoading,
    currentLogs: restUiState.currentLogs,
    onCloseLogsModal: restUiState.handleCloseLogsModal,
    initialAiContent: initialAiContentForModal,
  };


  return (
    <div
      className={`order-details-container ${restUiState.isUploading ? "uploading" : ""}`}
      style={{ fontSize: "1.05rem", padding: "20px" }}
    >
      {/* Przycisk "Wróć do listy" pozostaje na górze */}
      <button className="back-button" onClick={handleGoBack} style={{ marginBottom: '15px' }}>
        &larr; Wróć do listy
      </button>
      {/* Przycisk "Wyślij projekt klientowi" został przeniesiony do OrderInfoSection */}

      <h2 style={{ fontSize: "1.2rem", marginBottom: 10 }}>Szczegóły Zamówienia</h2>

      <OrderInfoSection {...orderInfoProps} />

      <h3 style={{ fontSize: "1.15rem", marginBottom: 10 }}>Pliki w zamówieniu</h3>
      <DropZone onImageDropped={actions.handleFileDropped} acceptAllFiles />

      <OrderFilesTable {...orderFilesTableProps} />

      <AddProjectSection {...addProjectSectionProps} />

      <ProductionFilesTable {...productionFilesTableProps} />

      {/* Render Modals and Sections */}
      {restUiState.showMetadataModal && <MetadataModal {...metadataModalProps} />}

      <MissingFontSection {...missingFontSectionProps} />

      <AiInteractionModals {...aiInteractionModalsProps} />

      {/* Renderowanie EditorOverlayWrapper, gdy jest potrzebny */}
      {showEditorOverlay && editorOverlayProps && (
        <EditorOverlayWrapper {...editorOverlayProps} />
      )}

      {/* Render the common Popup component directly */}
      <Popup config={uiState.popupConfig} onClose={hidePopup} />

    </div>
  );
}
